import Box from '@mui/material/Box';
import PostSearchSelectionScreen from './DetailPanelScreens/PostSearchSelectionScreen';
import Typography from '@mui/material/Typography';
import BradyLink from '../BradyLink';
import NoSelectionDetailsScreen from './DetailPanelScreens/NoSelectionDetailsScreen';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ProductDescriptionDetailScreen from './DetailPanelScreens/ProductDescriptionDetailScreen';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useContext, useEffect, useState } from 'react';
import { DetailPanelType } from '../../enum/DetailPanelTypeEnum';
import { MessageContext } from '../../context/MessageContext';
import { SearchTopic, SearchTopics } from '../../enum/SearchTopics';

interface DetailPanelProps {
  selectedOption: SearchTopic;
  data: any;
}

const ExampleMaterialData = {
  title: 'Example Title',
  applications: 'Applications',
  specialFeatures: 'Special Features',
  activeFlag: 1,
  adhesive_Type: null,
  b_Number: 'B-681/B-883',
  chemical_Resistance__Mixed_: null,
  chemical_Resistance__No_: null,
  chemical_Resistance__Yes_: null,
  color:
    'White on Green,Yellow on Orange,White on Blue,Red on Orange,Red,Green,Black on Yellow,Orange,Orange on Yellow,Blue,White on Red,Green on Yellow,Black,White on Brown,Black on Orange,Red on Yellow,Green on Orange',
  compatible_Surface_Types: null,
  finish: null,
  format: null,
  illumination: null,
  indoor_or_Outdoor_Use: 'Outdoor Use',
  label_Properties: null,
  laminated: 'Laminated',
  material: 'Polyester Overlaminate on Fiberglass Plastic Carrier',
  material_Properties: 'Rigid,High Visibility',
  maximum_Service_Temperature__F: 230,
  minimum_Service_Temperature__F: -40,
  pSPA_Photoluminescence_Class: null,
  primary_Ribbon: null,
  print_Technology: null,
  reflective_Type: null,
  resistance_Properties: 'Abrasion-resistant,Chemical-resistant',
  rowCreatedDt: 'Thu, 29 Aug 2024 14:53:05 GMT',
  rowUpdatedDt: 'Fri, 30 Aug 2024 08:54:32 GMT',
  shelf_Life: null,
  substrate: null,
  thickness: null,
};

const ExampleProductData = {
  imageLink: 'https://d37iyw84027v1q.cloudfront.net/na/bradyid/BradyID_Medium/95700.jpg',
  type: 'Key Cabinet',
  Unique_Selling_Proposition_USP:
    'Heavy-duty steel key cabinets from Brady® provide safe storage and controls access to keys.',
  group: 'Padlock & Key Racks & Carriers',
  description:
    'Heavy-duty steel key cabinets from Brady® provide safe storage and controls access to keys. Features pre-numbered, snap-hook key tags and a numerical index and 10 "checkout" tags to record who has borrowed keys. Two keys are supplied for each cabinet lock.',
  features: [
    'Features pre-numbered, snap-hook key tags and a numerical index and 10 "checkout" tags to record who has borrowed keys',
    'Two keys are supplied for each cabinet lock',
  ],
  dimensions: '12.125 in H x 8 in W x 2.625 in D',
  color: 'Tan',
  name: '95700: WALL-MOUNTED CABINET - 30 KEY CAPACITY',
  productId: '95700',
  link: '/lockout-tagout/stations-cabinets/30-key-cabinet-pid-95700?Ntt=95700&el=products&el_text=95700',
};

const HiddenColumns = ['activeFlag', 'rowCreatedDt', 'rowUpdatedDt', 'b_Number', 'link', 'materialId'];

// lists the fields that should be listed at the top of the details panel and the order in which they should be shown
const prioritizedFieldsInOrder = ['title', 'applications', 'specialFeatures'];

// lists the fields that should not be converted to lists
const fieldsThatShouldNotBeLists = ['title', 'applications', 'specialFeatures'];

const DetailPanel = (props: DetailPanelProps) => {
  const { selectedOption, data } = props;
  const [panelType, setPanelType] = useState<DetailPanelType>(DetailPanelType.None);
  const [currentDataOption, setCurrentDataOption] = useState<any>(data);
  const [usingExampleData, setUsingExampleData] = useState<boolean>(false);
  const [dataFields, setDataFields] = useState<string[]>([]);
  const { messages } = useContext(MessageContext);
  const hasMessages = messages.length > 0;

  const determineDetailPanelType = () => {
    if (selectedOption === SearchTopics.MaterialSearch) {
      setPanelType(DetailPanelType.Material);
      if (!data) {
        setCurrentDataOption(ExampleMaterialData);
        setUsingExampleData(true);
      }
    } else if (selectedOption === SearchTopics.ProductSearch) {
      setPanelType(DetailPanelType.Product);
      if (!data) {
        setCurrentDataOption(ExampleProductData);
        setUsingExampleData(true);
      }
    } else if (selectedOption === SearchTopics.None) {
      setPanelType(DetailPanelType.None);
      setCurrentDataOption(data);
      setUsingExampleData(false);
    }
  };

  useEffect(() => {
    determineDetailPanelType();
  }, [selectedOption]);

  useEffect(() => {
    if (data) {
      setCurrentDataOption(data);
      setUsingExampleData(false);
    } else {
      determineDetailPanelType();
    }
  }, [data]);

  useEffect(() => {
    if (currentDataOption) {
      createDataFieldsList(currentDataOption);
    }
  }, [currentDataOption]);

  const createDataFieldsList = (data: any) => {
    const fieldsWithoutPrioritizedFields = Object.keys(data).filter((key) => !prioritizedFieldsInOrder.includes(key));

    const orderedFieldsList = [...prioritizedFieldsInOrder, ...fieldsWithoutPrioritizedFields];

    setDataFields(orderedFieldsList);
  };

  return (
    <>
      {hasMessages && !data ? (
        <PostSearchSelectionScreen />
      ) : (
        <>
          <Box sx={{ flex: '0 0 auto' }}>
            {usingExampleData && panelType !== DetailPanelType.None && (
              <>
                <Typography variant="h2">Showing Example Data</Typography>
                <Typography variant="body1" mb={1}>
                  The following data is an example of how the details panel will look when a{' '}
                  {panelType !== DetailPanelType.Material ? 'product' : 'material'} is selected from the chat.
                </Typography>
              </>
            )}
            {panelType !== DetailPanelType.None && (
              <BradyLink
                type={panelType}
                id={panelType !== DetailPanelType.Material ? currentDataOption.productId : currentDataOption.materialId}
                bNumber={panelType === DetailPanelType.Material ? data?.materialId : data?.materialNumber}
                isInDistillery={data?.isInDistillery}
              />
            )}
          </Box>
          {panelType === DetailPanelType.None && <NoSelectionDetailsScreen />}
          <Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
            {panelType === DetailPanelType.Material && (
              <Grid container spacing={2} height={'0px'}>
                {/* height={'0px'} is a workaround for how the Split component handles height/overflow */}
                {dataFields.map((key) => {
                  const value = currentDataOption[key];
                  let convertedValue: any = value;
                  let valueArray = null;
                  if (typeof value === 'string') {
                    let tempArray = value.split(',');
                    if (tempArray.length > 1 && !fieldsThatShouldNotBeLists.includes(key)) {
                      valueArray = tempArray;
                    }
                  } else if (Array.isArray(value)) {
                    valueArray = value;
                  }
                  if ((!value || HiddenColumns.indexOf(key) >= 0) && !prioritizedFieldsInOrder.includes(key)) {
                    return null;
                  }

                  let smSize = valueArray && valueArray.length > 3 ? 12 : 6;
                  let colCount = valueArray && valueArray.length > 3 ? 2 : 1;

                  const result = key.replace(/([A-Z])/g, ' $1');
                  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);

                  if ((!convertedValue || convertedValue === '') && !prioritizedFieldsInOrder.includes(key)) {
                    return null;
                  }

                  return (
                    <Grid
                      key={`${key}_${value}`}
                      size={{
                        xs: 12,
                        sm: smSize
                      }}>
                      <Paper style={{ padding: '16px' }}>
                        <Typography variant="h6">{finalResult.replaceAll('_', ' ')}</Typography>
                        <ListItemText
                          secondary={
                            valueArray ? (
                              <List dense disablePadding sx={{ columns: colCount }}>
                                {valueArray.map((subItem, subIndex) => (
                                  <ListItem key={subIndex} disableGutters>
                                    <ListItemIcon style={{ minWidth: '20px' }}>
                                      <FiberManualRecordIcon
                                        style={{
                                          fontSize: '8px',
                                          color: 'rgba(0, 0, 0, 0.54)',
                                        }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary={subItem} />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              convertedValue
                            )
                          }
                        />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {panelType === DetailPanelType.Product && <ProductDescriptionDetailScreen data={currentDataOption} />}
          </Box>
        </>
      )}
    </>
  );
};

export default DetailPanel;
